/**
 * SegmentedControl Component
 * Button group for filters with separate chips or joined segments
 * @param {string} variant - 'separate' (default chips) or 'joined' (connected segments)
 * @param {boolean} multiSelect - Allow selecting multiple options
 * @param {boolean} iconOnly - Show only icons (labels on hover via title)
 * An option may carry a `title`: a note shown on hover (when the labels are shown).
 * An option may carry a `marker`: words such as "unsaved changes", drawn as a small dot after the
 * label and read out with the label (the dot alone would be colour only). Additive: an option
 * without one is drawn as before.
 */
import React from "react";
import PropTypes from "prop-types";
import "./SegmentedControl.scss";

export const SegmentedControl = ({
  value,
  onChange,
  options = [],
  variant = "joined",
  className = "",
  multiSelect = false,
  iconOnly = false,
}) => {
  const handleClick = (optionValue) => {
    if (multiSelect) {
      // Toggle the option in the array
      const newValue = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue];
      onChange(newValue);
    } else {
      onChange(optionValue);
    }
  };

  const isActive = (optionValue) => {
    return multiSelect ? value.includes(optionValue) : value === optionValue;
  };

  return (
    <div
      className={`segmented-control segmented-control--${variant} ${
        iconOnly ? "segmented-control--icon-only" : ""
      } ${className}`}
    >
      {options.map((option) => (
        <button
          key={option.value}
          className={`segmented-control__button ${
            isActive(option.value) ? "segmented-control__button--active" : ""
          }`}
          onClick={() => handleClick(option.value)}
          title={iconOnly ? option.label : option.title}
        >
          {option.icon && <span className="segmented-control__icon">{option.icon}</span>}
          {!iconOnly && option.label}
          {!iconOnly && option.count !== undefined && (
            <span className="segmented-control__count">({option.count})</span>
          )}
          {option.marker && (
            <>
              <span className="segmented-control__marker" aria-hidden="true" />
              <span className="visually-hidden"> ({option.marker})</span>
            </>
          )}
        </button>
      ))}
    </div>
  );
};

SegmentedControl.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      count: PropTypes.number,
      marker: PropTypes.string,
    })
  ).isRequired,
  variant: PropTypes.oneOf(["separate", "joined"]),
  className: PropTypes.string,
  multiSelect: PropTypes.bool,
  iconOnly: PropTypes.bool,
};
